import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldPlus, UserCog, Trash2, ArrowUpCircle, ArrowDownCircle, X, Check, KeyRound, Copy } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Field, Btn, Loading, Empty, handleErr } from "./AdminUI";
import { useAuth } from "@/context/AuthContext";

const LABELS = { view: "View", create: "Create/Upload", edit: "Edit", verify: "Verify", approve: "Approve/Publish", checkin: "Check-in" };

function PermGrid({ modules, value, onChange }) {
  const toggle = (mod, action, on) => {
    const cur = new Set(value[mod] || []);
    on ? cur.add(action) : cur.delete(action);
    onChange({ ...value, [mod]: [...cur] });
  };
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {Object.entries(modules).map(([mod, actions]) => (
        <div key={mod} className="rounded-lg bg-[#0A0C10] border border-[#273042] p-3">
          <div className="text-white text-sm font-bold capitalize mb-2">{mod}</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {actions.map((a) => (
              <label key={a} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={(value[mod] || []).includes(a)} onChange={(e) => toggle(mod, a, e.target.checked)}
                  data-testid={`perm-${mod}-${a}`} className="w-4 h-4 accent-[#E11D2A]" />
                {LABELS[a] || a}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminStaff() {
  const { user } = useAuth();
  const [modules, setModules] = useState({});
  const [reserved, setReserved] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", phone: "", email: "", displayName: "", permissions: {} });
  const [editing, setEditing] = useState(null);
  const [loginCode, setLoginCode] = useState(null); // {name, phone, code, expiresIn}

  const load = async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([endpoints.adminStaffModules(), endpoints.adminStaff()]);
      setModules(m.data.modules || {});
      setReserved(m.data.reserved || []);
      setStaff(s.data || []);
    } catch (e) { handleErr(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.username) { toast.error("Username required"); return; }
    if (!form.password || form.password.length < 4) { toast.error("Password (min 4 chars) required"); return; }
    if (!form.phone && !form.email) { toast.error("Add a mobile and email for secure OTP login (you can skip and add later, but staff then can't log in)"); }
    try {
      await endpoints.adminCreateStaff(form);
      toast.success("Staff account created");
      setForm({ username: "", password: "", phone: "", email: "", displayName: "", permissions: {} }); setCreating(false); load();
    } catch (e) { handleErr(e); }
  };
  const saveEdit = async () => {
    try {
      const payload = { displayName: editing.displayName, permissions: editing.permissions };
      if (editing.username) payload.username = editing.username;
      if (editing.newPassword) payload.password = editing.newPassword;
      if (editing.phone !== undefined) payload.phone = editing.phone;
      if (editing.email !== undefined) payload.email = editing.email;
      await endpoints.adminUpdateStaff(editing.id, payload);
      toast.success("Staff updated"); setEditing(null); load();
    } catch (e) { handleErr(e); }
  };
  const toggleActive = async (u) => { try { await endpoints.adminUpdateStaff(u.id, { active: !u.active }); toast.success(u.active ? "Deactivated" : "Activated"); load(); } catch (e) { handleErr(e); } };
  const promote = async (u) => { if (!window.confirm(`Promote ${u.displayName || u.phone} to Main Admin? They will get full access.`)) return; try { await endpoints.adminPromoteStaff(u.id); toast.success("Promoted to Main Admin"); load(); } catch (e) { handleErr(e); } };
  const demote = async (u) => { if (!window.confirm(`Demote ${u.displayName || u.phone} to Staff?`)) return; try { await endpoints.adminDemoteStaff(u.id); toast.success("Demoted to Staff"); load(); } catch (e) { handleErr(e); } };
  const del = async (u) => { if (!window.confirm(`Delete ${u.displayName || u.phone}?`)) return; try { await endpoints.adminDeleteStaff(u.id); toast.success("Deleted"); load(); } catch (e) { handleErr(e); } };
  const getLoginCode = async (u) => {
    try {
      const { data } = await endpoints.adminStaffLoginCode(u.id);
      setLoginCode({ name: u.displayName || u.phone, phone: data.phone, code: data.code, expiresIn: data.expiresIn });
    } catch (e) { handleErr(e); }
  };

  const admins = staff.filter((s) => s.role === "admin");
  const members = staff.filter((s) => s.role === "staff");

  return (
    <div>
      <PageHeader title="Staff & Access" desc="Create staff, grant granular permissions, and manage approvals access">
        <Btn onClick={() => setCreating((v) => !v)} data-testid="add-staff-btn"><ShieldPlus className="w-4 h-4" /> New staff</Btn>
      </PageHeader>

      <Card className="p-4 mb-6 text-xs text-slate-400">
        <span className="text-white font-semibold">Locked to Main Admin only:</span> Finance / Payments, Reports, Settings, Customer management, and all <span className="text-red-400 font-semibold">Delete</span> actions. Reserved actions ({reserved.join(", ")}) can never be granted to staff.
      </Card>

      {creating && (
        <Card className="p-5 mb-6 grid gap-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Username (staff logs in with this)" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="e.g. BESOFT" data-testid="staff-username-input" />
            <Field label="Password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 4 characters" data-testid="staff-password-input" />
            <Field label="Full name (optional)" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} data-testid="staff-name-input" />
            <Field label="Phone (for OTP login)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+250…" data-testid="staff-phone-input" />
            <Field label="Email (for OTP login)" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@bbkigali.com" data-testid="staff-email-input" />
          </div>
          <div className="text-xs text-slate-400 -mt-1">The staff signs in with the <span className="text-white font-semibold">Staff login</span> button (top of the site) using this username &amp; password — it takes them straight to their dashboard.</div>
          <div><div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Permissions</div>
            <PermGrid modules={modules} value={form.permissions} onChange={(p) => setForm({ ...form, permissions: p })} /></div>
          <Btn onClick={create} data-testid="staff-save-btn" className="w-max">Create staff account</Btn>
        </Card>
      )}

      {loading ? <Loading /> : (
        <div className="space-y-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Main Admins ({admins.length})</div>
            <div className="grid gap-3">
              {admins.map((u) => (
                <Card key={u.id} data-testid={`staff-row-${u.id}`} className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0"><div className="text-white font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#E11D2A]" /> {u.displayName || u.phone}</div><div className="text-xs text-slate-400">{u.phone} · Main Admin (full access)</div></div>
                  {u.id !== user?.id && <Btn variant="ghost" onClick={() => demote(u)} data-testid={`demote-${u.id}`}><ArrowDownCircle className="w-4 h-4" /> Demote</Btn>}
                </Card>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Staff ({members.length})</div>
            {members.length === 0 ? <Empty text="No staff yet. Create one above." /> : (
              <div className="grid gap-3">
                {members.map((u) => (
                  <Card key={u.id} data-testid={`staff-row-${u.id}`} className="p-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-white font-semibold flex items-center gap-2">{u.displayName || u.phone}
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${u.active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>{u.active ? "ACTIVE" : "INACTIVE"}</span></div>
                        <div className="text-xs text-slate-400">{u.username ? <>@{u.username}{u.phone ? ` · ${u.phone}` : ""}</> : (u.phone || "—")}</div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {Object.entries(u.permissions || {}).map(([m, acts]) => (
                            <span key={m} className="text-[11px] px-2 py-0.5 rounded bg-white/5 text-slate-300 border border-[#273042]"><b className="capitalize">{m}</b>: {acts.join(", ")}</span>
                          ))}
                          {Object.keys(u.permissions || {}).length === 0 && <span className="text-[11px] text-slate-500">No permissions assigned</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Btn variant="ghost" onClick={() => getLoginCode(u)} data-testid={`login-code-${u.id}`}><KeyRound className="w-4 h-4" /> Login code</Btn>
                        <Btn variant="ghost" onClick={() => setEditing({ ...u, permissions: { ...(u.permissions || {}) } })} data-testid={`edit-staff-${u.id}`}><UserCog className="w-4 h-4" /> Edit</Btn>
                        <Btn variant="ghost" onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`}>{u.active ? "Deactivate" : "Activate"}</Btn>
                        <Btn variant="ghost" onClick={() => promote(u)} data-testid={`promote-${u.id}`}><ArrowUpCircle className="w-4 h-4" /></Btn>
                        <button onClick={() => del(u)} data-testid={`delete-staff-${u.id}`} className="w-9 h-9 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {loginCode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="login-code-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setLoginCode(null)} />
          <div className="relative w-full max-w-sm rounded-2xl bg-[#121620] border border-[#273042] p-6 text-center">
            <div className="flex items-center justify-between mb-3"><h3 className="text-lg font-bold text-white flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#E11D2A]" /> Staff login code</h3><button onClick={() => setLoginCode(null)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <p className="text-sm text-slate-400 mb-1">Share with <span className="text-white font-semibold">{loginCode.name}</span></p>
            <p className="text-xs text-slate-500 mb-4">They open the app → Sign in → enter <span className="text-white">{loginCode.phone}</span> → then this code.</p>
            <div className="text-4xl font-extrabold tracking-[0.4em] text-[#F5B301] bg-[#0A0C10] border border-[#273042] rounded-xl py-4 mb-3" data-testid="login-code-value">{loginCode.code}</div>
            <button onClick={() => { navigator.clipboard?.writeText(loginCode.code); toast.success("Code copied"); }} data-testid="copy-login-code" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"><Copy className="w-4 h-4" /> Copy code</button>
            <p className="text-[11px] text-slate-500 mt-4">Valid for {Math.round((loginCode.expiresIn || 300) / 60)} minutes. Generate a new one anytime.</p>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#121620] border border-[#273042] p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-bold text-white">Edit {editing.displayName || editing.username || editing.phone}</h3><button onClick={() => setEditing(null)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <Field label="Username" value={editing.username || ""} onChange={(e) => setEditing({ ...editing, username: e.target.value })} data-testid="edit-username" />
              <Field label="New password (blank = keep)" type="text" value={editing.newPassword || ""} onChange={(e) => setEditing({ ...editing, newPassword: e.target.value })} placeholder="leave blank to keep" data-testid="edit-password" />
              <Field label="Full name" value={editing.displayName || ""} onChange={(e) => setEditing({ ...editing, displayName: e.target.value })} className="sm:col-span-2" />
              <Field label="Phone (OTP login)" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder="+250…" data-testid="edit-phone" />
              <Field label="Email (OTP login)" type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} placeholder="name@bbkigali.com" data-testid="edit-email" />
            </div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Permissions</div>
            <PermGrid modules={modules} value={editing.permissions} onChange={(p) => setEditing({ ...editing, permissions: p })} />
            <div className="flex gap-3 mt-5"><Btn onClick={saveEdit} data-testid="save-staff-edit-btn"><Check className="w-4 h-4" /> Save changes</Btn><Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn></div>
          </div>
        </div>
      )}
    </div>
  );
}
